import type { Database } from 'sql.js';
import type {
  TelemetryLocalStorePort,
  TelemetryLocalUserRef,
} from '@core/domain/ports/driven/TelemetryLocalStorePort.ts';

const INSERT_OBSERVABILIDAD_HTTP = `INSERT INTO observabilidad_http (tipo, correlacion_id, metodo, url, codigo_estado, duracion_ms, usuario_id) VALUES (?, ?, ?, ?, ?, ?, ?)`;
/** Safe json. */


function safeJson(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}
/** Correlation from context. */


function correlationFromContext(context: Record<string, unknown> | undefined): string | null {
  const c = context?.correlationId;
  return typeof c === 'string' ? c : null;
}

/** Persist telemetry in `observabilidad_*` tables of the same sql.js DB used elsewhere offline. */
export class SqlJsTelemetryLocalStore implements TelemetryLocalStorePort {
  /** Constructs this instance. */

  constructor(
    private readonly getDb: () => Database | undefined,
    private readonly notifyPersist: () => void
  ) {}
  /** Touch. */


  private touch(): void {
    try {
      this.notifyPersist();
    } catch {
      /* optional persistence; do not block telemetry */
    }
  }
  /** Write log. */


  writeLog(
    level: 'info' | 'error',
    message: string,
    context: Record<string, unknown> | undefined,
    user: TelemetryLocalUserRef
  ): void {
    const db = this.getDb();
    if (!db) return;
    const correlacionId = correlationFromContext(context);
    const usuarioId = user?.id ?? null;
    const detalle = safeJson({
      context: context ?? {},
      user: user ?? undefined,
      pageUrl: globalThis.location?.href,
    });
    try {
      db.run(
        `INSERT INTO observabilidad_logs (nivel, mensaje, detalle_json, correlacion_id, usuario_id) VALUES (?, ?, ?, ?, ?)`,
        [level, message, detalle, correlacionId, usuarioId]
      );
      this.touch();
    } catch {
      /* avoid blocking UI if schema is not migrated */
    }
  }
  /** Write event. */


  writeEvent(
    eventName: string,
    payload: Record<string, unknown> | undefined,
    user: TelemetryLocalUserRef,
    meta?: { correlationId?: string; pageUrl?: string | null }
  ): void {
    const db = this.getDb();
    if (!db) return;
    const body = safeJson({ payload: payload ?? {}, user: user ?? undefined });
    try {
      db.run(
        `INSERT INTO observabilidad_eventos (nombre_evento, payload_json, usuario_id, correlacion_id, url_pagina) VALUES (?, ?, ?, ?, ?)`,
        [eventName, body, user?.id ?? null, meta?.correlationId ?? null, meta?.pageUrl ?? null]
      );
      this.touch();
    } catch {
      /* */
    }
  }
  /** Write trace. */


  writeTrace(
    spanName: string,
    ok: boolean,
    durationMs: number,
    meta?: {
      errorMessage?: string;
      correlationId?: string;
      pageUrl?: string | null;
      user?: TelemetryLocalUserRef;
    }
  ): void {
    const db = this.getDb();
    if (!db) return;
    try {
      db.run(
        `INSERT INTO observabilidad_trazas (nombre_span, estado, duracion_ms, error_mensaje, usuario_id, correlacion_id, url_pagina) VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [
          spanName,
          ok ? 'ok' : 'error',
          durationMs,
          meta?.errorMessage ?? null,
          meta?.user?.id ?? null,
          meta?.correlationId ?? null,
          meta?.pageUrl ?? null,
        ]
      );
      this.touch();
    } catch {
      /* */
    }
  }
  /** Write metric. */


  writeMetric(
    kind: 'counter' | 'histogram',
    name: string,
    value: number,
    attributes: Record<string, string | number | boolean> | undefined,
    user: TelemetryLocalUserRef
  ): void {
    const db = this.getDb();
    if (!db) return;
    try {
      db.run(
        `INSERT INTO observabilidad_metricas (tipo, nombre, valor, atributos_json, usuario_id) VALUES (?, ?, ?, ?, ?)`,
        [kind, name, value, safeJson(attributes ?? {}), user?.id ?? null]
      );
      this.touch();
    } catch {
      /* */
    }
  }
  /** Write http request. */


  writeHttpRequest(
    meta: { correlationId: string; method: string; url: string },
    user: TelemetryLocalUserRef
  ): void {
    const db = this.getDb();
    if (!db) return;
    try {
      db.run(INSERT_OBSERVABILIDAD_HTTP, [
        'request',
        meta.correlationId,
        meta.method,
        meta.url,
        null,
        null,
        user?.id ?? null,
      ]);
      this.touch();
    } catch {
      /* */
    }
  }
  /** Write http response. */


  writeHttpResponse(
    meta: { correlationId: string; status: number; durationMs: number; url: string },
    user: TelemetryLocalUserRef
  ): void {
    const db = this.getDb();
    if (!db) return;
    try {
      db.run(INSERT_OBSERVABILIDAD_HTTP, [
        'response',
        meta.correlationId,
        null,
        meta.url,
        meta.status,
        meta.durationMs,
        user?.id ?? null,
      ]);
      this.touch();
    } catch {
      /* */
    }
  }
}
